/**
 * Per-entity configuration cluster for Engine Editor — substrate × input
 * cell per scope-doc §2 cell-by-cell routing. 5 sub-region cards tiled
 * within cluster cell per Atlas Day 2 visual-layout commitment A.1 + (b).
 *
 * Cluster header inherits Atlas PerEntityCluster.tsx verbatim per scope-doc
 * §3.2 copy principle (read-altitude cluster framing shared between Atlas
 * + Engine Editor).
 *
 * Per scope-doc §2 cell routing (lines 227-232):
 *   - Per-distributor: identity 4b plain + credit_limit/payment_terms 4b-deferred
 *     + per-distributor params overrides 4b-deferred (Day 88 CF 64 trigger)
 *   - Per-supplier: identity 4b plain + overrides 4b-deferred (Day 88 CF 64)
 *   - Per-brand: identity 4b plain + `brands.enforces_map` 4a cross-cell
 *     to MAP enforcement decision rule region
 *   - Per-manufacturer: identity 4b plain + `manufacturer_distributors` 4b-deferred
 *   - Per-category: identity 4b plain + overrides 4b-deferred (Day 88 CF 64)
 *
 * Day 1 scaffolding: positive 4a affordance for Per-brand `brands.enforces_map`
 * wires at Day 3 (column-surgical extraction). All sub-regions render Day-1
 * negative-affordance content per scope-doc §4.2 sub-shape vocabulary
 * verbatim copy framing.
 *
 * Sub-region components live as private const components inline at Day 1
 * per build-prompt P4.3 (no separate sub-region files yet).
 */

import type { JSX } from 'react';
import { Truck, Package, Tag, Factory, Layers } from 'lucide-react';
import {
  URL_DIRECTORY_DISTRIBUTORS,
  URL_DIRECTORY_SUPPLIERS,
  URL_DIRECTORY_BRANDS,
  URL_DIRECTORY_MANUFACTURERS,
  URL_DIRECTORY_CATEGORIES,
  URL_SETTINGS_PARAMS,
  URL_SETTINGS_CHANNELS_AND_PATHS,
} from '../../../lib/engine-editor/engineEditorUrls';

// ── Sub-region: Per-distributor ─────────────────────────────────────────────
function PerDistributorSubRegion(): JSX.Element {
  return (
    <div
      id="per-distributor"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <Truck size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Per-distributor</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Distributor identity (name, address, contact) + credit terms (`credit_limit`,
            `payment_terms`) + per-distributor params overrides.
          </p>
        </div>
      </div>
      <p className="text-xs text-ink-muted italic pt-1">
        Edit identity at{' '}
        <a
          href={URL_DIRECTORY_DISTRIBUTORS}
          className="text-accent hover:underline not-italic"
        >
          Directory → Distributors
        </a>
        . `credit_limit` + `payment_terms` edit at Directory → Distributors;
        Engine Editor absorbs at V2 (build-window). Per-distributor params
        overrides edit at{' '}
        <a
          href={URL_SETTINGS_PARAMS}
          className="text-accent hover:underline not-italic"
        >
          Settings → Params &amp; Defaults
        </a>
        ; Engine Editor absorbs at V2 (Day 88 CF 64 substrate-gate).
      </p>
    </div>
  );
}

// ── Sub-region: Per-supplier ────────────────────────────────────────────────
function PerSupplierSubRegion(): JSX.Element {
  return (
    <div
      id="per-supplier"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <Package size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Per-supplier</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Supplier identity (name, contact, terms) + per-supplier params overrides
            (expand via Day 88 CF 64 substrate-gate).
          </p>
        </div>
      </div>
      <p className="text-xs text-ink-muted italic pt-1">
        Edit identity at{' '}
        <a
          href={URL_DIRECTORY_SUPPLIERS}
          className="text-accent hover:underline not-italic"
        >
          Directory → Suppliers
        </a>
        . Per-supplier overrides Engine Editor absorbs at V2 (Day 88 CF 64
        substrate-gate).
      </p>
    </div>
  );
}

// ── Sub-region: Per-brand ───────────────────────────────────────────────────
// brands.enforces_map is V1 4a cross-cell to MAP enforcement (Day 3 ships positive affordance).
function PerBrandSubRegion(): JSX.Element {
  return (
    <div
      id="per-brand"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <Tag size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Per-brand</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Brand identity (name, image, supplier relationships) + MAP enforcement flag
            (`brands.enforces_map`). MAP enforcement flag also surfaces at MAP
            enforcement decision rule region.
          </p>
        </div>
      </div>
      <p className="text-xs text-ink-muted italic pt-1">
        Edit identity at{' '}
        <a
          href={URL_DIRECTORY_BRANDS}
          className="text-accent hover:underline not-italic"
        >
          Directory → Brands
        </a>
        . `brands.enforces_map` Engine Editor edit affordance wires at Day 3
        (column-surgical extraction per scope-doc §3.7); rendered at MAP
        enforcement decision rule region as primary edit surface.
      </p>
    </div>
  );
}

// ── Sub-region: Per-manufacturer ────────────────────────────────────────────
function PerManufacturerSubRegion(): JSX.Element {
  return (
    <div
      id="per-manufacturer"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <Factory size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Per-manufacturer</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Manufacturer identity + `manufacturer_distributors` eligibility relationship
            (which distributors source which manufacturers).
          </p>
        </div>
      </div>
      <p className="text-xs text-ink-muted italic pt-1">
        Edit identity at{' '}
        <a
          href={URL_DIRECTORY_MANUFACTURERS}
          className="text-accent hover:underline not-italic"
        >
          Directory → Manufacturers
        </a>
        . `manufacturer_distributors` edit at{' '}
        <a
          href={URL_SETTINGS_CHANNELS_AND_PATHS}
          className="text-accent hover:underline not-italic"
        >
          Settings → Channels &amp; Paths
        </a>
        ; Engine Editor absorbs at V2 (build-window).
      </p>
    </div>
  );
}

// ── Sub-region: Per-category ────────────────────────────────────────────────
function PerCategorySubRegion(): JSX.Element {
  return (
    <div
      id="per-category"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <Layers size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Per-category</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Category identity + per-category params overrides (expand via Day 88 CF 64
            substrate-gate).
          </p>
        </div>
      </div>
      <p className="text-xs text-ink-muted italic pt-1">
        Edit identity at{' '}
        <a
          href={URL_DIRECTORY_CATEGORIES}
          className="text-accent hover:underline not-italic"
        >
          Directory → Categories
        </a>
        . Per-category overrides Engine Editor absorbs at V2 (Day 88 CF 64
        substrate-gate).
      </p>
    </div>
  );
}

// ── Cluster wrapper ─────────────────────────────────────────────────────────

export default function EngineEditorPerEntityCluster(): JSX.Element {
  return (
    <section
      id="per-entity"
      data-cell-coordinate="substrate-input"
      data-region-type="cluster"
      className="border border-border bg-surface-1 rounded-lg p-4 space-y-3"
    >
      {/* Cluster header per Atlas §6.1 verbatim inheritance */}
      <div>
        <h2 className="text-sm font-semibold text-ink-bright">Per-entity configuration</h2>
        <p className="text-xs text-ink-secondary mt-1">
          Five entity types with config rows shaping engine inputs.
        </p>
        <p className="text-xs text-ink-secondary mt-1">
          <span className="font-semibold text-ink-bright">Together:</span> ISG&apos;s
          structural posture for who and what it buys through.
        </p>
      </div>

      {/* COMPOSABILITY: per-section "Last changed X days ago" indicator slot — wires post-Day 92 */}

      <div className="space-y-2">
        <PerDistributorSubRegion />
        <PerSupplierSubRegion />
        <PerBrandSubRegion />
        <PerManufacturerSubRegion />
        <PerCategorySubRegion />
      </div>
    </section>
  );
}
